import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@folio/components';
import { useQuery } from '@tanstack/react-query';
import {
  useDistributionRequestControllerCreateManyDistributionRequests,
  viewSpecificControllerListBrokerageFirmDistributionRequestsQueryOptions,
} from 'korint-sdk';
import { useKorintProductConfig } from 'korint-sdk/react';
import { Check } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface DistributionRequestListCreationButtonProps {
  /**
   * The brokerage firm id.
   */
  brokerageFirmId: string;

  /**
   * The title to be displayed in the creation button.
   */
  title: string;
}

export function DistributionRequestListCreationButton({
  brokerageFirmId,
  title,
}: DistributionRequestListCreationButtonProps) {
  const { t } = useTranslation();
  const { productsConfig } = useKorintProductConfig();

  const { data: distributionRequestsProducts, status: getDistributionRequestsProductsStatus } =
    useQuery({
      ...viewSpecificControllerListBrokerageFirmDistributionRequestsQueryOptions(brokerageFirmId),
      select: data => data.data.map(distributionRequest => distributionRequest.productId),
    });

  const { mutateAsync: createDistributionRequests, status: createDistributionRequestsStatus } =
    useDistributionRequestControllerCreateManyDistributionRequests();

  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  function handleSelect(productId: string) {
    setSelectedProductIds(prev =>
      prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId],
    );
  }

  const options = Object.keys(productsConfig)
    .filter(productId => !distributionRequestsProducts?.includes(productId))
    .map(productId => ({
      value: productId,
      label: t(`products.${productId}.label`),
    }));

  if (!options.length) {
    return null;
  }

  return (
    <DropdownMenu
      open={isOpen}
      onOpenChange={open => {
        if (!open) {
          setSelectedProductIds([]);
        }

        setIsOpen(open);
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          variant="default"
          loading={
            getDistributionRequestsProductsStatus === 'pending' ||
            createDistributionRequestsStatus === 'pending'
          }
        >
          {title}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {options.map(({ label, value }) => (
          <div key={value}>
            <DropdownMenuItem
              onSelect={e => {
                e.preventDefault();
                handleSelect(value);
              }}
              className="flex items-center justify-between cursor-pointer"
            >
              <span>{label}</span>
              {selectedProductIds.includes(value) && (
                <Check className="w-4 h-4 text-primary ml-2" />
              )}
            </DropdownMenuItem>
          </div>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Button
            size="sm"
            variant="ghost"
            className="w-full"
            onClick={async () => {
              setIsOpen(false);
              await createDistributionRequests({
                data: {
                  brokerageFirmId,
                  productIds: selectedProductIds,
                },
              });
              setSelectedProductIds([]);
            }}
          >
            {t('distributionRequestList.creationButton.validate')}
          </Button>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
